import React, { useEffect, useLayoutEffect, useRef, useState, FC } from 'react';
import {
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  Box,
  Typography,
  useTheme,
  SelectChangeEvent,
  SelectProps as MuiSelectProps,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import clsx from 'clsx';

interface Option {
  label: string;
  value: string;
}

interface SelectProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  id: string;
  error?: boolean;
  helperText?: string;
  options?: Option[];
  disabled?: boolean;
  sx?: object;
  MenuProps?: MuiSelectProps['MenuProps'];
}

const Select: FC<SelectProps> = ({
  label,
  value: propValue,
  onChange,
  id,
  error = false,
  helperText,
  options = [{ label: '1', value: '1' }],
  disabled = false,
  sx = {},
  MenuProps = {},
}) => {
  const theme = useTheme();

  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [value, setValue] = useState(propValue || '');
  const [isCustomValue, setIsCustomValue] = useState(false);
  const prevValueRef = useRef(propValue);

  useEffect(() => {
    setIsOptionSelected(value !== '');
  }, [value]);

  useLayoutEffect(() => {
    if (propValue !== prevValueRef.current) {
      setValue(propValue);
      prevValueRef.current = propValue;
    }
  }, [propValue]);

  useEffect(() => {
    setIsCustomValue(!options.some((option) => option.value === value));
  }, [value, options]);

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    event.stopPropagation();
    const newValue = event.target.value;
    onChange(newValue);
    setIsOptionSelected(newValue !== '');
  };

  const mergedMenuProps = {
    ...MenuProps,
    sx: {
      '.MuiMenu-paper': {
        marginTop: '6px',
        borderRadius: '12px',
        px: '8px',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: 'none',
        '& .MuiMenuItem-root': {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
          '&.Mui-selected': {
            backgroundColor: '#C1FE40',
            '&:hover': {
              backgroundColor: '#C1FE40',
            },
          },
        },
      },
      ...MenuProps.sx,
    },
  };

  return (
    <FormControl
      variant="filled"
      disabled={disabled}
      sx={{
        width: '100%',
        position: 'relative',
        direction: 'rtl',
        ...sx,
        '.MuiFilledInput-root': {
          backgroundColor: '#F2F4F7',
          borderRadius: 4,
          textAlign: 'right',
          '&:hover': {
            backgroundColor: '#F2F4F7',
            borderColor: null,
          },
          '&.Mui-focused': {
            backgroundColor: '#F2F4F7',
            borderColor: null,
          },
          '&:before, &:after': {
            content: 'none',
          },
        },
        '.MuiInputLabel-root': {
          color: `${error ? '#E53E3E' : 'black'}`,
          position: 'absolute',

          left: 'auto',
          right: 18,
          top: error ? '46.5%' : '70%',
          transform: isOptionSelected
            ? 'translate(0%, -140%) scale(0.75)'
            : 'translate(0%,-100%)',
          transition: theme.transitions.create(['top', 'transform', 'right'], {
            duration: theme.transitions.duration.shorter,
          }),
          '&.Mui-focused': {
            color: `${error ? '#E53E3E' : theme.palette.text.secondary}`,
            transformOrigin: 'top right',
          },
        },
      }}
    >
      <InputLabel
        shrink={isOptionSelected}
        htmlFor={id}
        sx={{
          fontSize: '16px',
          fontWeight: '700',
          fontFamily: 'AlmoniDL',
          textAlign: 'right',
          transformOrigin: 'top right',
        }}
      >
        {label}
      </InputLabel>
      <MuiSelect
        labelId={`${id}-label`}
        id={id}
        value={value}
        onChange={handleSelectChange}
        displayEmpty
        notched={false}
        error={error}
        className={clsx({ 'option-selected': isOptionSelected })}
        MenuProps={mergedMenuProps}
        sx={{
          '.MuiSelect-select': {
            fontFamily: 'AlmoniDL !important',
            fontWeight: 700,
            textAlign: 'right',
            pr: '32px !important',
            pl: '8px !important',
            '&:focus': {
              backgroundColor: 'transparent',
            },
          },
          '&:before, &:after': {
            borderBottom: 'none',
          },
          '.MuiSelect-icon': {
            transition: theme.transitions.create(['top'], {
              duration: theme.transitions.duration.shorter,
            }),
            right: 'unset',
            left: 12,
          },
          '&.option-selected .MuiSelect-icon': {
            top: 'calc(50% - 3px)',
          },
        }}
      >
        {isCustomValue && (
          <MenuItem value={value} disabled>
            {value}
          </MenuItem>
        )}
        {options.map((option, index) => (
          <MenuItem
            sx={{
              borderRadius: '8px',
              fontFamily: 'AlmoniDL',
              fontWeight: '700',
              justifyContent: 'flex-end',
            }}
            key={index}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </MuiSelect>
      {error && (
        <Box sx={{ display: 'flex', alignItems: 'center', mt: '8px' }}>
          <ErrorOutlineIcon
            sx={{ color: '#E53E3E', mr: '5px', height: '14px', width: '14px' }}
          />
          <Typography
            variant="body1"
            fontWeight="fontWeightRegular"
            fontSize={14}
            sx={{ color: '#E53E3E' }}
          >
            {helperText}
          </Typography>
        </Box>
      )}
    </FormControl>
  );
};

export default Select;
